import { call, put, race, take } from 'redux-saga/effects';
import { SagaIterator } from '@redux-saga/types';
import { eventChannel } from 'redux-saga';
import io, { Socket } from 'socket.io-client';
import { Profile, User } from '@/api';
import {
  paymentActions,
  profileActions,
  userActions,
  amlTraceActions,
  amlTraceHistoryActions,
  serviceOptionsActions,
} from '@/redux/actions';
import { PriceListPlansData } from '@/redux/models';
import { push } from 'redux-first-history';
import { notifyProVersion } from '@/helpers';
import { TraceTypeEnum, COMMON_CONTAINER_ID } from '@/config';
import { store } from '@/redux/store';
import { toast } from 'react-toastify';

const EVENT_USER = 'user-updates';
const EVENT_PROFILE = 'profile-updates';
const EVENT_PLANS = 'plan-updates';
const EVENT_REFRESH_PLANS = 'refresh-plans';
const EVENT_TERMS = 'terms-updates';
const EVENT_DELETE = 'user-deactivate';
const EVENT_PARTIAL_UPDATES = 'partial-updates';
const EVENT_TRACE_UPDATES = 'trace-updates';

function* handleUserData(data: Record<string, any>) {
  const eventData = data as User;
  yield put(userActions.updateAttribute(eventData));
  yield put(
    paymentActions.setDiscounts({
      discount: eventData?.discount || 0,
      discountDueTime: eventData?.discountDueTime || 0,
    }),
  );
}

function* handleTermsData(data: Record<string, any>) {
  const eventData = data as User;

  if (Object.prototype.hasOwnProperty.call(eventData, 'termsConfirmRequired')) {
    yield put(
      userActions.updateAttribute({
        termsConfirmRequired: eventData.termsConfirmRequired,
      }),
    );
  }
}

function* handleProfileData(data: Record<string, any>) {
  const eventData = data as Partial<Profile>;
  yield put(profileActions.updateAttribute(eventData));
}

function* handlePlansData(data: Record<string, any>) {
  const eventData = data as PriceListPlansData;

  yield put(paymentActions.setPaymentPlans(eventData));
  yield put(paymentActions.setBiggestPricePerCheck(eventData));
}

function* handleRefreshPlans(data: Record<string, any>) {
  yield put(serviceOptionsActions.fetchOptions());
  yield put(paymentActions.fetchPriceListPlans());
}

function* handleDeactivate(data: Record<string, any>) {
  const eventData = data;

  if (!!eventData.deactivated) {
    yield put(push('/'));
    notifyProVersion('signOut');
    window.location.reload();
  }
}

function* handleTraceUpdates(data: Record<string, any>) {
  const { uuid, ...rest } = data;

  if (!uuid) {
    return;
  }

  if (uuid) {
    yield put(amlTraceActions.addTraceUpdate({ uuid, data: rest }));
  }

  if (rest.type === TraceTypeEnum.ERROR && rest.message) {
    toast.error(rest.message, {
      containerId: COMMON_CONTAINER_ID,
    });
  }

  if ([TraceTypeEnum.RESULT, TraceTypeEnum.ERROR].includes(rest.type)) {
    setTimeout(() => {
      store.dispatch(amlTraceHistoryActions.fetchTraceHistory({}));
    }, 1000);
  }
}

function* handlePartialUpdates(data: Record<string, any>) {
  const eventData = data as User;
  const userUpdates: Record<string, any> = {};

  if (Object.prototype.hasOwnProperty.call(eventData, 'use2Fa')) {
    userUpdates['use2Fa'] = eventData.use2Fa;
  }

  if (Object.prototype.hasOwnProperty.call(eventData, 'is2faEnabled')) {
    userUpdates['is2faEnabled'] = eventData.is2faEnabled;
  }

  if (Object.prototype.hasOwnProperty.call(eventData, 'is2faAllowed')) {
    userUpdates['is2faAllowed'] = eventData.is2faAllowed;
  }

  if (Object.keys(userUpdates).length) {
    yield put(userActions.updateAttribute(userUpdates));
  }
}

const createWebSocketConnection = () => {
  const socket = io(`${import.meta.env.VITE_REACT_APP_AMLBOT_API_URL}`, {
    transports: ['websocket', 'polling'],
    withCredentials: true,
  });

  return socket;
};

function joinRoom(socket: Socket) {
  socket.emit('post', { url: '/join' });
}

function* watchSignout(socket: Socket): SagaIterator {
  while (true) {
    yield take(userActions.signout.type);
    socket.disconnect();
  }
}

const createSocketChannel = (socket: Socket) => {
  return eventChannel((emit) => {
    // Reconnect
    socket.on('connect', () => {
      joinRoom(socket);
    });

    const userListener = (event: any) => {
      emit({ name: EVENT_USER, data: event });
    };

    const profileListener = (event: any) => {
      emit({ name: EVENT_PROFILE, data: event });
    };

    const plansListener = (event: any) => {
      emit({ name: EVENT_PLANS, data: event });
    };

    const refreshPlansListener = (event: any) => {
      emit({ name: EVENT_REFRESH_PLANS, data: event });
    };

    const serviceTermsListener = (event: any) => {
      emit({ name: EVENT_TERMS, data: event });
    };

    const userDeleteListener = (event: any) => {
      emit({ name: EVENT_DELETE, data: event });
    };

    const userPartialUpdatesListener = (event: any) => {
      emit({ name: EVENT_PARTIAL_UPDATES, data: event });
    };

    const traceUpdatesListener = (event: any) => {
      emit({ name: EVENT_TRACE_UPDATES, data: event });
    };

    const errorListener = (errorEvent: any) => {
      emit(new Error(errorEvent.reason));
    };

    socket.on(EVENT_USER, userListener);
    socket.on(EVENT_PROFILE, profileListener);
    socket.on(EVENT_PLANS, plansListener);
    socket.on(EVENT_REFRESH_PLANS, refreshPlansListener);
    socket.on(EVENT_TERMS, serviceTermsListener);
    socket.on(EVENT_DELETE, userDeleteListener);
    socket.on(EVENT_PARTIAL_UPDATES, userPartialUpdatesListener);
    socket.on(EVENT_TRACE_UPDATES, traceUpdatesListener);
    socket.on('error', errorListener);

    // the subscriber must return an unsubscribe function
    // this will be invoked when the saga calls `channel.close` method
    const unsubscribe = () => {
      socket.off(EVENT_USER, userListener);
      socket.off(EVENT_PROFILE, profileListener);
      socket.off(EVENT_PLANS, plansListener);
      socket.off(EVENT_REFRESH_PLANS, refreshPlansListener);
      socket.off(EVENT_TERMS, serviceTermsListener);
      socket.off(EVENT_DELETE, userDeleteListener);
      socket.off(EVENT_PARTIAL_UPDATES, userPartialUpdatesListener);
      socket.off(EVENT_TRACE_UPDATES, traceUpdatesListener);
      socket.off('error', errorListener);
    };

    return unsubscribe;
  });
};

function* handleEventData(payload: Record<string, any>): SagaIterator {
  const { name, data } = payload;

  switch (name) {
    case EVENT_USER:
      yield call(handleUserData, data);
      break;
    case EVENT_PROFILE:
      yield call(handleProfileData, data);
      break;
    case EVENT_TERMS:
      yield call(handleTermsData, data);
      break;
    case EVENT_PLANS:
      yield call(handlePlansData, data);
      break;
    case EVENT_REFRESH_PLANS:
      yield call(handleRefreshPlans, data);
      break;
    case EVENT_DELETE:
      yield call(handleDeactivate, data);
      break;
    case EVENT_PARTIAL_UPDATES:
      yield call(handlePartialUpdates, data);
      break;
    case EVENT_TRACE_UPDATES:
      yield call(handleTraceUpdates, data);
      break;
    default:
      break;
  }
}

function* listenEvents(socket: Socket): SagaIterator {
  const socketChannel = yield call(createSocketChannel, socket);

  while (true) {
    try {
      const payload = yield take(socketChannel);
      yield call(handleEventData, payload);
    } catch (err) {
      // socketChannel is still open in catch block
      // if we want end the socketChannel, we need close it explicitly
      socketChannel.close();

      // Restart channel
      yield call(listenEvents, socket);
    }
  }
}

function* initiate(): SagaIterator {
  try {
    const socket = yield call(createWebSocketConnection);
    yield race([call(listenEvents, socket), call(watchSignout, socket)]);
  } catch (error: unknown) {
    // console.log(error);
  }
}

export { initiate };
